import objectFitVideos from 'object-fit-videos';

const io = new IntersectionObserver(onIntersection, {
  rootMargin: '50px 0px',
  threshold: 0.01,
});

function onIntersection(entries) {
  entries.forEach((entry) => {
    if (!entry.intersectionRatio) {
      return;
    }

    if ('onIntersection' in entry.target) {
      entry.target.onIntersection();
    }
  });
}

class WPBackgroundVideo extends HTMLElement {
  constructor(self) {
    self = super(self);
    this._video = null;
    this._placeholder = this.querySelector('wp-img');
    return self;
  }

  /**
   * Tag name of element
   * @readonly
   * @static
   */
  static get is() {
    return 'wp-background-video';
  }

  /**
   * Getter for `animation` attribute
   */
  get animation() {
    return this.hasAttribute('animation')
      ? this.getAttribute('animation')
      : 'fadeIn';
  }

  /**
   * Setter for `animation` attribute
   */
  set animation(value) {
    if (value) {
      this.setAttribute('animation', value);
    } else {
      this.removeAttribute('animation');
    }
  }

  /**
   * Getter for `position` attribute
   */
  get position() {
    return this.hasAttribute('position')
      ? this.getAttribute('position')
      : '50% 50%';
  }

  /**
   * Setter for `position` attribute
   */
  set position(value) {
    if (value) {
      this.setAttribute('position', value);
    } else {
      this.removeAttribute('position');
    }
  }

  /**
   * Getter for `playing` attribute
   */
  get playing() {
    return this.hasAttribute('playing');
  }

  /**
   * Setter for `playing` attribute
   */
  set playing(value) {
    if (Boolean(value)) {
      this.setAttribute('playing', '');
    } else {
      this.removeAttribute('playing');
    }
  }

  /**
   * Getter for `play-count` attribute
   */
  get playCount() {
    return this.hasAttribute('play-count')
      ? parseInt(this.getAttribute('play-count'))
      : null;
  }

  /**
   * Setter for `play-count` attribute
   */
  set playCount(value) {
    if (Number(value)) {
      this.setAttribute('play-count', parseInt(value));
    } else {
      this.removeAttribute('play-count');
    }
  }

  /**
   * Getter for `src` attribute
   */
  get src() {
    return this.hasAttribute('src') ? this.getAttribute('src') : '';
  }

  /**
   * Setter for `src` attribute
   */
  set src(value) {
    if (value) {
      this.setAttribute('src', value);
    } else {
      this.removeAttribute('src');
    }
  }

  /**
   * Lifecycle event when element is attached to DOM
   */
  connectedCallback() {
    // If no video, return
    if (!this.src) {
      console.warn('No src attribute.');
      return;
    }

    // Add video
    this._addVideo();

    // If no placeholder, set opacity to zero (don't rely on classes alone)
    if (!this._placeholder) {
      this.style.opacity = '0';
    }

    // Object fit video
    this._applyObjectFit(this._video);

    // Add to observer
    io.observe(this);
  }

  /**
   * Lifecycle event when element is detach from DOM
   */
  disconnectedCallback() {
    io.unobserve(this);
  }

  onIntersection() {
    this._loadVideo();
    io.unobserve(this);
  }

  _loadVideo() {
    this._video.style.display = 'block';
    this._video.setAttribute('webkit-playsinline', '');
    this._video.setAttribute('playsinline', '');
    this._video.setAttribute('muted', '');
    this._video.load();

    // Check if Video is ready to play...
    this._video.addEventListener(
      'canplay',
      () => {
        if (this.playing) {
          return;
        }

        // If not playing already, play video
        if (!this._isPlaying) {
          this._video.play();
        }

        this.playing = true;

        // Fade in video
        if (this._placeholder) {
          this._placeholder.style.transition = 'opacity 1s ease';
          this._placeholder.style.opacity = '0';
        } else {
          this.classList.add(this.animation);
        }
      },
      true,
    );

    // Remove video once ended
    if (this.playCount) {
      let i = 0;

      this._video.addEventListener(
        'ended',
        () => {
          // If hasn't hit playcout, play again
          if (i !== this.playCount - 1) {
            this._video.play();
            i++;
            return;
          }

          this._video.style.transition = 'opacity 1s 1s ease';
          this._video.style.opacity = '0';

          this._video.$once('transitionend', () => {
            this.parentNode.removeChild(this);
          });
        },
        false,
      );
    }
  }

  _applyObjectFit(video) {
    video.style.objectFit = 'cover';
    video.style.objectPosition = this.position;
    video.style.fontFamily = `
      object-fit: cover; 
      object-position: ${this.position}`;
    video.style.height = '100%';
    video.style.width = '100%';
    video.style.display = 'block';

    objectFitVideos(video);
  }

  get _isPlaying() {
    return (
      this._video.currentTime > 0 &&
      !this._video.paused &&
      !this._video.ended &&
      this._video.readyState > 2
    );
  }

  get _source() {
    const arr = this.src.split(',');
    let html = '';

    arr.forEach((src) => {
      html += `<source src="${src}" type="video/${src.split('.').pop()}">`;
    });

    return html;
  }

  _addVideo() {
    const video = document.createElement('video');

    video.height = '100%';
    video.width = '100%';
    video.innerHTML = this._source;
    video.style.display = 'none';
    video.muted = true;
    video.loop = this.playCount ? false : true;

    this.insertBefore(video, this.firstChild);
    this._video = video;
  }

  pause() {
    if (!this._isPlaying) {
      return;
    }

    this._video.pause();
    this.playing = false;
  }

  play() {
    if (this._isPlaying) {
      return;
    }

    this._video.play();
    this.playing = true;
  }
}

customElements.define(WPBackgroundVideo.is, WPBackgroundVideo);
